import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  statGrid: {
    marginTop: theme.spacing(1),
  },
  statCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(0.5),
  },
  statValue: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  chip: {
    margin: theme.spacing(0.25),
    fontSize: "0.75rem",
    height: 24,
  },
  headerCell: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

function StatCard({ label, value, classes }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <div className={classes.statCard}>
        <Typography className={classes.statValue}>{value}</Typography>
        <Typography className={classes.statLabel}>{label}</Typography>
      </div>
    </Grid>
  );
}

function extractObservationsFromBatches(batches, steamId) {
  const observations = [];
  for (const batch of batches) {
    const rawObs = batch?.rawBatch?.Observations || [];
    for (const obs of rawObs) {
      if (obs.SteamID === steamId || !steamId) {
        observations.push({
          ...obs,
          matchId: batch.resolvedMatchId || batch.payloadMatchId,
          receivedAt: batch.receivedAt,
        });
      }
    }
  }
  return observations;
}

export default function PlayerDrillIn({
  steamId,
  summary,
  batches = [],
}) {
  const classes = useStyles();
  const observations = extractObservationsFromBatches(batches, steamId);

  return (
    <>
      <Paper className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Telemetry Summary
        </Typography>
        {summary ? (
          <Grid container spacing={1} className={classes.statGrid}>
            <StatCard
              label="Total Kills"
              value={summary.totalKills ?? 0}
              classes={classes}
            />
            <StatCard
              label="Total Deaths"
              value={summary.totalDeaths ?? 0}
              classes={classes}
            />
            <StatCard
              label="HS Rate"
              value={
                summary.headshotRate != null
                  ? `${(summary.headshotRate * 100).toFixed(1)}%`
                  : "—"
              }
              classes={classes}
            />
            <StatCard
              label="Wallbang Kills"
              value={summary.totalWallbangKills ?? 0}
              classes={classes}
            />
            <StatCard
              label="Smoke Kills"
              value={summary.totalSmokeKills ?? 0}
              classes={classes}
            />
            <StatCard
              label="NoScope Kills"
              value={summary.totalNoScopeKills ?? 0}
              classes={classes}
            />
            <StatCard
              label="Airborne Kills"
              value={summary.totalAirborneKills ?? 0}
              classes={classes}
            />
            <StatCard
              label="Batches"
              value={summary.batchCount ?? 0}
              classes={classes}
            />
            <StatCard
              label="Matches"
              value={summary.matchCount ?? 0}
              classes={classes}
            />
          </Grid>
        ) : (
          <Typography className={classes.emptyState}>
            No telemetry summary available for this player.
          </Typography>
        )}
      </Paper>

      <Paper className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Behavioral Observations ({observations.length})
        </Typography>
        <Divider style={{ marginBottom: 8 }} />
        {observations.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Tag</TableCell>
                  <TableCell className={classes.headerCell}>Category</TableCell>
                  <TableCell className={classes.headerCell}>Message</TableCell>
                  <TableCell className={classes.headerCell}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {observations.slice(0, 100).map((obs, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Chip
                        label={obs.Tag || "unknown"}
                        size="small"
                        className={classes.chip}
                        color={
                          obs.Category === "behavioral_profile"
                            ? "secondary"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.mono}>
                      {obs.Category || "—"}
                    </TableCell>
                    <TableCell>
                      {obs.Message || "—"}
                    </TableCell>
                    <TableCell className={classes.mono}>
                      {obs.matchId
                        ? String(obs.matchId).substring(0, 12) + "…"
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography className={classes.emptyState}>
            No observations recorded for this player.
          </Typography>
        )}
      </Paper>
    </>
  );
}
